// Saisie de devis branchée sur le catalogue du site boutique (via /api/catalog/*, pont côté backend).
// - ComboInput : liste déroulante filtrée en tapant les premiers mots (saisie libre possible)
// - ProductLine : ligne de devis qui propose les produits du site (prix, promo, stock) ou l'ajout au site
// - VariantPicker : choix de la déclinaison (stockage, couleur, état…) d'un produit du site
// - StockSiteCard : après validation du devis, propose d'ajouter les quantités au stock du site
import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { X, Plus, Link2, Loader2, PackagePlus, Check, Tag, Palette } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// minuscules sans accents, pour comparer « ecran » et « Écran »
const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const euros = (c) => (c / 100).toFixed(2).replace(".", ",") + " €";

// Ligne de devis : remise PAR UNITÉ (€), bornée au prix — même calcul que backend/routes/quote_lines.py
export const lineDiscount = (i) => Math.min(Math.max(parseFloat(i.discount) || 0, 0), Math.max(parseFloat(i.unit_price) || 0, 0));
export const lineTotal = (i) => ((parseFloat(i.unit_price) || 0) - lineDiscount(i)) * (parseFloat(i.quantity) || 1);

// Champs d'une ligne reliée au site : prix AVANT promo + promo en remise
const depuisSite = (centimesAvant, centimesApres, promoLabel) => {
  const remise = Math.max(0, (centimesAvant || 0) - (centimesApres || 0));
  return {
    unit_price: ((centimesAvant || centimesApres || 0) / 100).toFixed(2),
    discount: remise > 0 ? (remise / 100).toFixed(2) : "",
    discount_label: remise > 0 && promoLabel ? `Promo site ${promoLabel}` : "",
  };
};

const STATUT_VARIANTE = {
  en_stock:     { t: "En stock",     c: "bg-emerald-500/15 text-emerald-400" },
  sur_commande: { t: "Sur commande", c: "bg-amber-500/15 text-amber-400" },
  rupture:      { t: "Rupture",      c: "bg-red-500/15 text-red-400" },
};

// ─────────────────────────────────────────────────────────────
// Liste déroulante filtrable : chaque mot tapé doit figurer dans l'option
export function ComboInput({ value, onChange, options = [], placeholder, disabled, emptyHint }) {
  const [open, setOpen] = useState(false);
  const [actif, setActif] = useState(0);
  const fermer = useRef(null);

  const filtrees = useMemo(() => {
    const mots = norm(value).split(/\s+/).filter(Boolean);
    const res = mots.length ? options.filter((o) => mots.every((m) => norm(o).includes(m))) : options;
    return res.slice(0, 60);
  }, [value, options]);

  const choisir = (o) => { onChange(o); setOpen(false); };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActif((i) => Math.min(i + 1, filtrees.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActif((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && open && filtrees[actif]) { e.preventDefault(); choisir(filtrees[actif]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative">
      <Input
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActif(0); }}
        onFocus={() => { clearTimeout(fermer.current); setOpen(true); }}
        onBlur={() => { fermer.current = setTimeout(() => setOpen(false), 150); }}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      {open && !disabled && (filtrees.length > 0 || emptyHint) && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-border bg-card shadow-lg">
          {filtrees.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">{emptyHint}</div>
          ) : filtrees.map((o, i) => (
            <button
              key={o}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choisir(o)}
              className={`block w-full text-left px-3 py-1.5 text-sm ${i === actif ? "bg-primary/20 text-foreground" : "text-foreground hover:bg-secondary/60"}`}
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Marques / modèles du site (liste curatée + modèles présents en base)
export function useCatalogMeta(actif) {
  const [meta, setMeta] = useState({ brands: [], modelsByBrand: {}, erreur: false });
  useEffect(() => {
    if (!actif || meta.brands.length) return;
    axios.get(`${API_URL}/api/catalog/meta`, { headers: headers() })
      .then((r) => setMeta({ brands: r.data.brands || [], modelsByBrand: r.data.modelsByBrand || {}, erreur: false }))
      .catch(() => setMeta((m) => ({ ...m, erreur: true })));
  }, [actif]); // eslint-disable-line react-hooks/exhaustive-deps
  return meta;
}

// Modèles d'une marque saisie (correspondance insensible à la casse)
export function modelesDe(meta, marque) {
  const cle = Object.keys(meta.modelsByBrand).find((b) => norm(b) === norm(marque));
  return cle ? meta.modelsByBrand[cle] : [];
}

// ─────────────────────────────────────────────────────────────
// Ligne de devis reliée au catalogue du site
export function ProductLine({ item, onChange, onRemove, brand, model, quoteType, quoteNumber }) {
  const [focus, setFocus] = useState(false);
  const [resultats, setResultats] = useState([]);
  const [elargi, setElargi] = useState(false);
  const [cherche, setCherche] = useState(false);
  const [ajout, setAjout] = useState(null); // { universe, condition, price } quand le mini-formulaire est ouvert
  const [envoi, setEnvoi] = useState(false);
  const fermer = useRef(null);
  const q = item.name || "";

  // Recherche (anti-rebond) dès que la ligne a le focus
  useEffect(() => {
    if (!focus) return;
    if (q.trim().length < 2 && !model) { setResultats([]); return; }
    const t = setTimeout(async () => {
      setCherche(true);
      try {
        const r = await axios.get(`${API_URL}/api/catalog/search`, {
          headers: headers(), params: { brand: brand || "", model: model || "", q: q.trim() },
        });
        setResultats(r.data.results || []);
        setElargi(!!r.data.elargi);
      } catch { setResultats([]); }
      finally { setCherche(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, brand, model, focus]);

  const [variantes, setVariantes] = useState(null); // { id, name } quand le choix de déclinaison est ouvert

  const choisir = (p) => {
    setFocus(false); setAjout(null);
    if (p.hasVariants) { setVariantes({ id: p.id, name: p.name }); return; }
    onChange({
      name: p.name, store_product_id: p.id, store_new: false, store_variant_id: null, variant_label: null,
      ...depuisSite(p.original ?? p.price, p.price, p.promoLabel),
    });
  };

  const choisirVariante = (produit, v) => {
    onChange({
      name: `${produit.name} — ${v.label}`, store_product_id: produit.id, store_new: false,
      store_variant_id: v.variantId, variant_label: v.label,
      ...depuisSite(v.original, v.price, v.promoLabel),
    });
    setVariantes(null);
  };

  const exact = resultats.some((p) => norm(p.name) === norm(q));
  const peutAjouter = q.trim().length >= 3 && !exact && !item.store_product_id;

  const ouvrirAjout = () => setAjout({
    universe: quoteType === "reparation" ? "pieces" : "appareils",
    condition: "neuf",
    price: parseFloat(item.unit_price) > 0 ? String(item.unit_price) : "",
  });

  const ajouterAuSite = async () => {
    const prix = parseFloat(String(ajout.price).replace(",", "."));
    if (!(prix > 0)) { toast.error("Indiquez un prix de vente"); return; }
    setEnvoi(true);
    try {
      const r = await axios.post(`${API_URL}/api/catalog/products`, {
        name: q.trim(), price: prix, brand: brand || null, model: model || null,
        universe: ajout.universe, condition: ajout.condition, source: quoteNumber ? `devis ${quoteNumber}` : "devis",
      }, { headers: headers() });
      const p = r.data.product;
      onChange({ name: p.name, unit_price: (p.price / 100).toFixed(2), store_product_id: p.id, store_new: !r.data.existant });
      toast.success(r.data.existant ? "Ce produit existait déjà sur le site : ligne reliée" : "Produit ajouté au site (brouillon à compléter)");
      setAjout(null); setFocus(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ajout au site impossible");
    } finally { setEnvoi(false); }
  };

  const montrerListe = focus && (cherche || resultats.length > 0 || peutAjouter);

  return (
    <div className="space-y-1">
      <div className="flex gap-2 items-center">
        <div className="flex-1 relative">
          <Input
            value={q}
            onChange={(e) => onChange({ name: e.target.value })}
            onFocus={() => { clearTimeout(fermer.current); setFocus(true); }}
            onBlur={() => { fermer.current = setTimeout(() => { if (!ajout) setFocus(false); }, 200); }}
            placeholder={model ? `Produit / pièce pour ${model}…` : "Produit ou prestation…"}
            className={item.store_product_id ? "pr-20" : ""}
            autoComplete="off"
          />
          {item.store_product_id && (
            <button type="button" title="Délier du produit du site"
              onClick={() => onChange({ store_product_id: null, store_new: false })}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-400 hover:bg-red-500/15 hover:text-red-400">
              <Link2 className="w-3 h-3" /> {item.store_new ? "ajouté" : "site"}
            </button>
          )}
        </div>
        <Input value={item.quantity} onChange={(e) => onChange({ quantity: e.target.value })} placeholder="Qté" className="w-14" type="number" min="1" />
        <Input value={item.unit_price} onChange={(e) => onChange({ unit_price: e.target.value })} placeholder="Prix €" className="w-24" type="number" step="0.01" />
        <Input value={item.discount ?? ""} onChange={(e) => onChange({ discount: e.target.value, discount_label: "" })}
          placeholder="Remise" title="Remise par unité (€)" type="number" step="0.01" min="0"
          className={`w-20 ${lineDiscount(item) > 0 ? "text-red-400 border-red-500/40" : ""}`} />
        <span className="w-20 text-right text-sm font-semibold text-foreground tabular-nums">{lineTotal(item).toFixed(2)}€</span>
        <button type="button" onClick={onRemove} className="text-red-400 hover:text-red-300 p-1"><X className="w-4 h-4" /></button>
      </div>

      {(item.variant_label || (lineDiscount(item) > 0 && item.discount_label)) && (
        <div className="flex flex-wrap items-center gap-2 pl-1 text-[11px]">
          {item.variant_label && (
            <button type="button" onClick={() => setVariantes({ id: item.store_product_id, name: (item.name || "").split(" — ")[0] })}
              className="inline-flex items-center gap-1 rounded bg-blue-500/15 px-1.5 py-0.5 font-medium text-blue-400 hover:bg-blue-500/25"
              title="Changer de déclinaison">
              <Palette className="w-3 h-3" /> {item.variant_label} · changer
            </button>
          )}
          {lineDiscount(item) > 0 && item.discount_label && (
            <span className="inline-flex items-center gap-1 rounded bg-red-500/15 px-1.5 py-0.5 font-medium text-red-400">
              <Tag className="w-3 h-3" /> {item.discount_label} : −{(lineDiscount(item) * (parseFloat(item.quantity) || 1)).toFixed(2)}€
            </span>
          )}
        </div>
      )}

      {variantes && (
        <VariantPicker produit={variantes} actuel={item.store_variant_id}
          onPick={(v) => choisirVariante(variantes, v)} onCancel={() => setVariantes(null)} />
      )}

      {montrerListe && (
        <div className="rounded-md border border-border bg-card/95 shadow-lg overflow-hidden"
             onMouseDown={(e) => { if (e.target.tagName !== "INPUT" && e.target.tagName !== "SELECT") e.preventDefault(); }}>
          {cherche && <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Recherche sur le site…</div>}
          {elargi && <div className="px-3 py-1.5 text-[11px] text-amber-400">Rien pour {model} : produits {brand} correspondants</div>}
          <div className="max-h-52 overflow-y-auto">
            {resultats.map((p) => (
              <button key={p.id} type="button" onClick={() => choisir(p)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-secondary/60">
                <span className="flex-1 truncate text-foreground">{p.name}</span>
                {(p.draft || !p.active) && <span className="text-[10px] rounded bg-amber-500/15 px-1.5 text-amber-400">{p.draft ? "brouillon" : "hors ligne"}</span>}
                {p.hasVariants && <span className="text-[10px] rounded bg-blue-500/15 px-1.5 text-blue-400">variantes ›</span>}
                {p.promoLabel && <span className="text-[10px] rounded bg-red-500/15 px-1.5 font-semibold text-red-400">{p.promoLabel}</span>}
                <span className={`text-[10px] rounded px-1.5 ${p.stock > 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-secondary text-muted-foreground"}`}>
                  {p.stock > 0 ? `stock ${p.stock}` : "stock 0"}
                </span>
                <span className="w-24 text-right">
                  {p.promoLabel && p.original > p.price && <span className="block text-[10px] leading-none text-muted-foreground line-through">{euros(p.original)}</span>}
                  <span className="font-semibold text-primary">{euros(p.price)}</span>
                </span>
              </button>
            ))}
            {!cherche && resultats.length === 0 && q.trim().length >= 2 && (
              <div className="px-3 py-2 text-xs text-muted-foreground">Aucun produit du site ne correspond.</div>
            )}
          </div>

          {peutAjouter && !ajout && (
            <button type="button" onClick={ouvrirAjout}
              className="w-full flex items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-blue-400 hover:bg-blue-500/10">
              <PackagePlus className="w-4 h-4" /> Ajouter « {q.trim()} » au site
            </button>
          )}

          {ajout && (
            <div className="border-t border-border p-3 space-y-2 bg-secondary/20">
              <p className="text-xs text-muted-foreground">
                Nouveau produit sur le site : <b className="text-foreground">{q.trim()}</b>
                {brand && <> · {brand}</>}{model && <> {model}</>} — créé en brouillon hors ligne (photo à ajouter dans l'admin du site).
              </p>
              <div className="grid grid-cols-3 gap-2">
                <select value={ajout.universe} onChange={(e) => setAjout((a) => ({ ...a, universe: e.target.value }))}
                  className="h-9 rounded-md border border-input bg-transparent px-2 text-sm text-foreground">
                  <option value="pieces" className="bg-card">Pièce détachée</option>
                  <option value="appareils" className="bg-card">Appareil / accessoire</option>
                </select>
                <select value={ajout.condition} onChange={(e) => setAjout((a) => ({ ...a, condition: e.target.value }))}
                  className="h-9 rounded-md border border-input bg-transparent px-2 text-sm text-foreground">
                  <option value="neuf" className="bg-card">Neuf</option>
                  <option value="reconditionne" className="bg-card">Reconditionné</option>
                  <option value="occasion" className="bg-card">Occasion</option>
                </select>
                <Input value={ajout.price} onChange={(e) => setAjout((a) => ({ ...a, price: e.target.value }))}
                  placeholder="Prix de vente €" type="number" step="0.01" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => { setAjout(null); setFocus(false); }}>Annuler</Button>
                <Button type="button" size="sm" className="text-xs bg-blue-600 hover:bg-blue-700" onClick={ajouterAuSite} disabled={envoi}>
                  {envoi ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Ajouter au site
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Choix de la déclinaison d'un produit du site : un bloc par type (Stockage, Couleur, État…),
// puis récapitulatif prix / promo / disponibilité de la combinaison retenue.
export function VariantPicker({ produit, actuel, onPick, onCancel }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState("");
  const [sel, setSel] = useState({});

  useEffect(() => {
    let annule = false;
    axios.get(`${API_URL}/api/catalog/variants/${produit.id}`, { headers: headers() })
      .then((r) => {
        if (annule) return;
        const d = r.data;
        setData(d);
        // présélection : la déclinaison déjà sur la ligne, sinon la première en stock, sinon la première
        const v = d.variants.find((x) => x.variantId === actuel)
          || d.variants.find((x) => x.statut === "en_stock") || d.variants[0];
        if (v) setSel(v.ids);
      })
      .catch((e) => !annule && setErreur(e.response?.data?.detail || "Déclinaisons indisponibles"));
    return () => { annule = true; };
  }, [produit.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupes = data?.groups || [];
  const variantes = data?.variants || [];
  // combinaison exacte : même option pour chaque type
  const trouver = (ids) => variantes.find((v) => groupes.every((g) => v.ids[g.type] === ids[g.type]));
  const courante = trouver(sel);

  // Cliquer une option : garde les autres choix si la combinaison existe, sinon prend la
  // combinaison la plus proche qui contient cette option.
  const choisir = (type, id) => {
    const voulu = { ...sel, [type]: id };
    if (trouver(voulu)) { setSel(voulu); return; }
    const candidats = variantes.filter((v) => v.ids[type] === id);
    if (!candidats.length) return;
    const score = (v) => groupes.filter((g) => v.ids[g.type] === sel[g.type]).length + (v.statut === "en_stock" ? 0.5 : 0);
    setSel([...candidats].sort((a, b) => score(b) - score(a))[0].ids);
  };

  return (
    <div className="rounded-xl border border-blue-500/40 bg-secondary/20 p-3 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {data?.product?.image && <img src={courante?.image || data.product.image} alt="" className="w-10 h-10 rounded-md object-cover bg-white shrink-0" />}
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Choisissez la déclinaison</p>
            <p className="text-sm font-semibold text-foreground truncate">{produit.name}</p>
          </div>
        </div>
        <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground p-1" title="Fermer"><X className="w-4 h-4" /></button>
      </div>

      {!data && !erreur && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Chargement des déclinaisons…</div>}
      {erreur && <p className="text-xs text-red-400">{erreur}</p>}
      {data && !variantes.length && <p className="text-xs text-amber-400">Aucune déclinaison vendable pour ce produit sur le site.</p>}

      {variantes.length > 0 && groupes.map((g) => {
        const estCouleur = g.items.some((o) => o.colorHex);
        return (
          <div key={g.type}>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</p>
            <div className="flex flex-wrap gap-2">
              {g.items.map((o) => {
                const actif = sel[g.type] === o.id;
                const avecAutres = trouver({ ...sel, [g.type]: o.id }); // combinaison avec les autres choix actuels
                const existe = variantes.some((v) => v.ids[g.type] === o.id);
                if (!existe) return null;
                if (estCouleur) {
                  return (
                    <button key={o.id} type="button" onClick={() => choisir(g.type, o.id)}
                      className={`flex flex-col items-center gap-1 rounded-lg border-2 px-2.5 py-1.5 transition ${actif ? "border-blue-500 bg-blue-500/15" : "border-border hover:border-blue-500/50"} ${avecAutres ? "" : "opacity-40"}`}>
                      <span className="relative h-7 w-7 rounded-full border border-white/30" style={{ background: o.colorHex || "#888" }}>
                        {actif && <Check className="absolute inset-0 m-auto w-4 h-4 text-white drop-shadow" />}
                      </span>
                      <span className="text-xs font-medium text-foreground">{o.label}</span>
                    </button>
                  );
                }
                return (
                  <button key={o.id} type="button" onClick={() => choisir(g.type, o.id)}
                    className={`min-w-[5.5rem] rounded-lg border-2 px-3 py-1.5 text-left transition ${actif ? "border-blue-500 bg-blue-500/15" : "border-border hover:border-blue-500/50"} ${avecAutres ? "" : "opacity-40"}`}>
                    <span className="block text-sm font-semibold text-foreground">{o.label}</span>
                    <span className="block text-[11px] text-muted-foreground">{avecAutres ? euros(avecAutres.price) : "autre combinaison"}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {variantes.length > 0 && (
        <div className="rounded-lg border border-border/60 bg-card/60 p-2.5 text-sm space-y-1">
          {courante ? (
            <>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-foreground">{courante.label}</span>
                <span className={`text-[11px] rounded px-1.5 py-0.5 ${STATUT_VARIANTE[courante.statut]?.c}`}>
                  {STATUT_VARIANTE[courante.statut]?.t}{courante.statut === "en_stock" ? ` (${courante.stock})` : ""}
                </span>
              </div>
              <div className="flex justify-between text-muted-foreground"><span>Prix</span><span>{euros(courante.original)}</span></div>
              {courante.discount > 0 && (
                <div className="flex justify-between text-red-400"><span>Promo site {courante.promoLabel}</span><span>−{euros(courante.discount)}</span></div>
              )}
              <div className="flex justify-between font-bold text-foreground border-t border-border/60 pt-1"><span>Prix client</span><span className="text-primary">{euros(courante.price)}</span></div>
            </>
          ) : <p className="text-xs text-amber-400">Cette combinaison n'est pas proposée sur le site.</p>}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" className="text-xs" onClick={onCancel}>Annuler</Button>
        <Button type="button" size="sm" className="text-xs bg-blue-600 hover:bg-blue-700" disabled={!courante} onClick={() => onPick(courante)}>
          <Check className="w-3.5 h-3.5 mr-1" /> Choisir cette déclinaison
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Après validation : « ajouter au stock du site ? »
export function StockSiteCard({ quote, onDone }) {
  const lignes = (quote.items || []).map((it, index) => ({ ...it, index })).filter((it) => it.store_product_id);
  const [choix, setChoix] = useState(() => Object.fromEntries(lignes.map((l) => [
    l.index, { coche: !!l.store_new && !l.stock_site_ajoute, quantite: String(parseInt(l.quantity, 10) || 1) },
  ])));
  const [envoi, setEnvoi] = useState(false);

  if (!lignes.length || quote.stock_site_traite || !["accepte", "facture"].includes(quote.status)) return null;

  const envoyer = async (ignorer) => {
    setEnvoi(true);
    try {
      const r = await axios.post(`${API_URL}/api/quotes/${quote.id}/stock-site`, {
        ignorer,
        lignes: ignorer ? [] : lignes.filter((l) => choix[l.index]?.coche)
          .map((l) => ({ index: l.index, quantite: parseInt(choix[l.index].quantite, 10) || 0 })),
      }, { headers: headers() });
      const ko = (r.data.resultats || []).filter((x) => !x.ok);
      if (ko.length) toast.error(ko.map((x) => `${x.name} : ${x.error}`).join(" · "));
      else if (!ignorer) toast.success("Stock du site mis à jour ✅");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur mise à jour du stock");
    } finally { setEnvoi(false); }
  };

  const aucun = !lignes.some((l) => choix[l.index]?.coche);

  return (
    <div className="p-3 rounded-xl border border-amber-500/40 bg-amber-500/10 space-y-2">
      <p className="text-sm font-semibold text-amber-300 flex items-center gap-2"><PackagePlus className="w-4 h-4" /> Ajouter au stock du site ?</p>
      <p className="text-xs text-muted-foreground">Devis validé. Cochez les produits à ajouter au stock de www.amcphone-martinique.fr (ex. pièces commandées pour l'occasion).</p>
      {lignes.map((l) => (
        <label key={l.index} className="flex items-center gap-2 text-sm text-foreground">
          <input type="checkbox" checked={!!choix[l.index]?.coche}
            onChange={(e) => setChoix((c) => ({ ...c, [l.index]: { ...c[l.index], coche: e.target.checked } }))} />
          <span className="flex-1 truncate">{l.name}{l.store_new && <span className="ml-1 text-[10px] text-blue-400">(ajouté depuis ce devis)</span>}</span>
          <Input value={choix[l.index]?.quantite} type="number" min="1" className="w-16 h-8"
            onChange={(e) => setChoix((c) => ({ ...c, [l.index]: { ...c[l.index], quantite: e.target.value } }))} />
        </label>
      ))}
      <div className="flex gap-2 justify-end pt-1">
        <Button type="button" variant="outline" size="sm" className="text-xs" disabled={envoi} onClick={() => envoyer(true)}>Non merci</Button>
        <Button type="button" size="sm" className="text-xs bg-amber-600 hover:bg-amber-700" disabled={envoi || aucun} onClick={() => envoyer(false)}>
          {envoi ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />} Ajouter au stock
        </Button>
      </div>
    </div>
  );
}
